<template>
  <div>

      <div class="row">
        <div class="col-md-6 pr-5">
          <h1 class="mt-0 se-std-bp-title-sb mb-3">Input Data: Form 8582 ({{this.taxFormYear}})
            <a class="btn-link se-page-help-icon" v-tooltip.top-center="'Learn More'" @click="showPageHelp = true">
              <i class="fa fa-info-circle"></i>
            </a>
          </h1>

          <form autocomplete="off">
            <div class="bp-form-group mr-5"
                 v-for="(row, index) in tableData">

              <template v-if="row.pageHeader">
                <h4>{{row.pageHeader}}</h4>
              </template>
             
              <template v-if="row.line && !isHidden[index]">

                <template v-if="row.id === 'netLoss8582RentalAmount'">
                  <p>
                    Some borrowers have business losses that exceed business income. Excess losses are reported in detail on IRS Form 8582 Passive Activity Loss Limitations.
                    Look for this form in the borrower’s individual (personal) tax returns.
                    To ensure you are reviewing the correct Form 8582, check that the Name shown of return in the header section of Form 8582 is associated to the borrower who owns the business.
                  </p>
                  <p>
                    Some borrowers have more than one business shown in Part IV and Part V. Check that the Name of activity shown in the first column is associated to the business you want
                    Income Calculator to evaluate. You may need to confirm with the borrower or their tax preparer if applicable.
                  </p>
                </template>

                <label :for="row.id" class="se-std-field-group-label">
                    <span>{{row.line}}</span>
                  </label>
                  <a class="se-help-icon btn-link" v-tooltip.top-center="'Learn More'" @click="handleFieldHelp(row.id)" v-show="row.hasHelp">
                    <i class="fa fa-info-circle"></i>
                  </a>
              </template>

              <div @mouseover="highlightHoveredOverFormField(row)" @mouseleave="highlightHoveredOverFormField()">

                    <template v-if="!isHidden[index]">
                      <radio :label="false"
                             v-model="row.isEmpty"
                             :disabled="isSkipped[index] || isLocked"
                             :class="{'opacity-5': isSkipped[index]}"
                             class="mb-1">
                        <span class="se-std-label">Enter {{row.name}}</span>
                      </radio>
                      <input :id="row.id"
                             :readonly="isLocked"
                             class="se-tax-field-input ml-4 bp-form-input bp-u-shadow-04"
                             type="text"
                             v-mask="row.mask"
                             @focus="highlightFocusedFormField(row)"
                             @blur="fieldIsFocused = false"
                             :disabled="row.isEmpty || isSkipped[index]"
                             v-model="row.value" />
                      <radio :label="true"
                             v-model="row.isEmpty"
                             :disabled="isSkipped[index] || isLocked"
                             :class="{'opacity-5': isSkipped[index]}"
                             class="mt-1"><span class="se-std-label">There is no Form 8582, or no amount is associated to the business that Income Calculator is evaluating</span>
                      </radio>

                  </template>
                </div>

            </div>
          </form>

          <div class="bp-help is-error mb-2" v-if="$v.allAnswered.$error">
            <div class="bp-ucon" data-src="feedback-warning"><svg class="bp-svg" viewBox="0 0 24 24" id="feedback-warning" xmlns="http://www.w3.org/2000/svg"><path fill="currentColor" d="M13.009 13.987V8.934h-1.956v5.053h1.956zm0 4.016v-1.962h-1.956v1.962h1.956zM2.988 20.962L12.016 3l9.025 17.962H2.988z"></path></svg>
            </div>
            <template v-if="!$v.allAnswered.required">Provide answer for all items or select 'There is no amount on this line'</template>
          </div>

        </div>

        <div class="col-md-6 d-none d-sm-none d-md-block">
          <div class="std-pdf-row">
          <WebViewer :url="this.pdf"
                     ref="viewer"
                     :se-fields="seFields"
                     :tax-form-year="this.taxFormYear"
                     :tax-form-id="this.taxFormId"
                     @viewerLoaded="onViewerLoaded"
                     :key="this.pdfViewerKey"/>
          </div>
        </div>

      </div>

    <se-help :visible.sync="showPageHelp">
      <p>
        Use this screen to provide information about Passive Activity Loss Limitations reported on Schedule E.  {{ appName }} will display Form 8582 for an S-Corporation 
        or Partnership when there is a Passive Activity Loss Amount on Line 28(g) or Nonpassive Activity Loss Amount on Line 28(i) of the borrower’s Schedule E. 
        At this time these amounts are not included in the qualifying income calculation.
      </p>
    </se-help>

    <se-help :visible.sync="showFieldHelp.netLoss8582RentalAmount">
      <div class="se-field-help">
        <b>FAQs:</b>
        <p>
          Q. I’m not used to considering this information when I evaluate income from self-employment, business ownership or rental property.
          Will {{ appName }} be using this information in its monthly qualifying income calculation?<br>
		  A. Not at this time. Income Calculator may collect information not traditionally used in evaluating self-employment income. 
          Some data may be used to validate data integrity or determine if income from other tax return forms or schedules may be needed.  
          In some cases, Fannie Mae may use this data for future research.
        </p>
      </div>
    </se-help>

    <se-help :visible.sync="showFieldHelp.netLoss8582OtherAmount">
      <div class="se-field-help">
        <b>FAQs:</b>
        <p>
          Q. I’m not used to considering this information when I evaluate income from self-employment, business ownership or rental property.
          Will {{ appName }} be using this information in its monthly qualifying income calculation?<br>
		  A. Not at this time. Income Calculator may collect information not traditionally used in evaluating self-employment income. 
          Some data may be used to validate data integrity or determine if income from other tax return forms or schedules may be needed.  
          In some cases, Fannie Mae may use this data for future research.
        </p>
      </div>
    </se-help>

    <se-buttons
      :display-submit="false"
      :display-save="false"
      :disable-submit="true"
      :disable-save="true"
      :disable-next="false"
      :disable-previous="false"
      @seNext="next"
      @sePrevious="previous">
    </se-buttons>

  </div>

</template>
<script>
import SeButtons from './SeButtons.vue'
import SeHelp from './SeHelp.vue'
import {Checkbox, Radio} from 'src/components/index'
import LSwitch from 'src/components/Switch.vue'
import { Collapse, CollapseItem } from 'element-ui'
import WebViewer from 'src/components/WebViewerROC.vue'
import {Table, TableColumn, Drawer} from 'element-ui'
import {required, requiredIf} from "vuelidate/lib/validators";
import Enums from 'src/model/enums'
import { taxSingleFormMixin } from 'src/mixins/TaxSingleFormMixin'
import appNameMixin from '../../../mixins/AppNameMixin'
export default {
  mixins: [taxSingleFormMixin, appNameMixin],
  components: {
    Checkbox,
    Radio,
    LSwitch,
    SeButtons,
    SeHelp,
    [Collapse.name]: Collapse,
    [CollapseItem.name]: CollapseItem,
    WebViewer,
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
    [Drawer.name]: Drawer,
  },
  data() {
    return {
    }
  },
  validations: {
    allAnswered: {
      required: (v) => v
    }
  },
  computed: {
    isSkipped() {
      return this.tableData.map(f => {
        if (this.skippedForm) return true
        return false;
      })
    },
    isHidden() {
      return this.tableData.map((f,index) => {
        return false
      })
    },
  },
  methods: {
    handleHelp(index, row) {
      row.showHelp = true;
    },
  }
}
</script>
<style >

.required > div:after {
  content:" *";
  color: red;
  text-nowrap: true
}

.std-pdf-row {
  position: fixed;
  width: 40%;
  height: 90%;
}


</style>
